import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Plane } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canViewOrgWideEmployeeBenefits } from '../utils/employeeBenefitsAccess';
import {
  createBlankPeopleTravelTrip,
  subscribePeopleTravelTrips,
} from '../utils/peopleTravelFirestore';
import { publishMyTripPlanDrafts } from '../utils/peopleTravelPlanService';
import type { PeopleTravelTrip } from '../types';
import '../components/pm/pm-shared.css';
import '../components/people/travel/PeopleTravel.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

const statusLabel = (s: PeopleTravelTrip['status']) => s.replace(/_/g, ' ');

const PeopleTravelPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [trips, setTrips] = useState<PeopleTravelTrip[]>([]);
  const [orgWide, setOrgWide] = useState(false);
  const [creating, setCreating] = useState(false);

  const canOrg = canViewOrgWideEmployeeBenefits({
    authRole: user?.role,
    designations: user?.designations,
  });

  React.useEffect(() => {
    if (!user?.id) return;
    void publishMyTripPlanDrafts().catch(() => undefined);
    return subscribePeopleTravelTrips({
      viewerUid: user.id,
      orgWide: canOrg && orgWide,
      onData: setTrips,
    });
  }, [user?.id, canOrg, orgWide]);

  const userName = useMemo(() => {
    const map = new Map(crmUsers.map(u => [u.uid, u.displayName || u.email || u.uid]));
    return (uid: string) => map.get(uid) || uid;
  }, [crmUsers]);

  const handleNewTrip = async () => {
    if (!user?.id || creating) return;
    setCreating(true);
    try {
      const tripId = await createBlankPeopleTravelTrip(user.id);
      navigate(`/people/travel/${tripId}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  if (!user) return null;

  return (
    <div className="people-hub-route-root">
      <div className="ops-hub-toolbar">
        <div className="ops-hub-toolbar-left">
          <div>
            <h2 className="ops-hub-title">Travel</h2>
            <p className="people-hub-subtitle">
              Plan longer-distance trips — flights, hotels, rental cars, itinerary, and budget. Not
              for routine local driving.
            </p>
          </div>
        </div>
        <div className="ops-hub-toolbar-right">
          <button
            type="button"
            className="pm-btn pm-btn-primary"
            disabled={creating}
            onClick={() => void handleNewTrip()}
          >
            <Plus size={16} /> {creating ? 'Opening board…' : 'New trip'}
          </button>
        </div>
      </div>

      <div className="people-hub-body pm-scroll">
        <div className="people-hub-panel">
          {canOrg && (
            <div className="people-travel-filter-row">
              <div className="ops-hub-views" role="group" aria-label="Trip scope">
                <button
                  type="button"
                  className={`ops-view-btn ${!orgWide ? 'active' : ''}`}
                  onClick={() => setOrgWide(false)}
                >
                  My trips
                </button>
                <button
                  type="button"
                  className={`ops-view-btn ${orgWide ? 'active' : ''}`}
                  onClick={() => setOrgWide(true)}
                >
                  All trips
                </button>
              </div>
            </div>
          )}

          {trips.length === 0 ? (
            <div className="people-hub-empty">
              <Plane size={32} />
              <p>No trips yet. New trip opens a board — tell the planner the overall chain.</p>
            </div>
          ) : (
            <table className="people-travel-table">
              <thead>
                <tr>
                  <th>Trip</th>
                  <th>Dates</th>
                  <th>Destinations</th>
                  <th>Status</th>
                  <th>Budget</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {trips.map(trip => (
                  <tr key={trip.id} onClick={() => navigate(`/people/travel/${trip.id}`)}>
                    <td>{trip.title}</td>
                    <td>
                      {trip.startDate && trip.endDate ? `${trip.startDate} → ${trip.endDate}` : '—'}
                    </td>
                    <td>
                      {trip.destinations.map((d: PeopleTravelTrip['destinations'][number]) => d.label).join(', ') ||
                        '—'}
                    </td>
                    <td>
                      <span className={`people-travel-status people-travel-status--${trip.status}`}>
                        {statusLabel(trip.status)}
                      </span>
                    </td>
                    <td>
                      ${trip.budget.selectedTotal.toLocaleString()}
                      {trip.budget.cap ? ` / $${trip.budget.cap.toLocaleString()}` : ''}
                    </td>
                    <td>{userName(trip.ownerUserId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeopleTravelPage;
